// Every mutation the Actions toolkit performs, each one reflecting its result
// back into the catalog and reporting through a toast.

import { useMemo } from 'react';
import { t } from '@lingui/macro';

import ActionsApi from '../../api/actions';
import { currentVersion } from './atUtils';
import {
  downloadJson,
  exportActionDocument,
  resolveCategoryUuid,
  resolveIntegrationUuids,
  schemaPayload,
} from './actionMutations';

const NO_SELECTION = { errors: [{ detail: 'No action selected' }] };

export default function useActionsCrud({
  actions,
  setActions,
  action,
  version,
  fetchAll,
  pushToast,
  setModal,
  onCreated,
}) {
  return useMemo(() => {
    async function createAction({ data }) {
      const response = await ActionsApi.createAction({
        name: data.name,
        alias: data.alias,
        description: data.description,
        category_uuid: await resolveCategoryUuid(data.category),
        ...schemaPayload(data),
      });
      if (response.data) {
        await fetchAll();
        onCreated(response.data.id);
        setModal(null);
        pushToast({
          kind: 'success',
          title: t`Action created`,
          msg: `${data.name} created on v1.`,
        });
      }
      return response;
    }

    async function editInPlace({ data }) {
      if (!action || !version) return NO_SELECTION;

      const response = await ActionsApi.updateAction(version.uuid, {
        alias: data.alias,
        description: data.description,
        category_uuid: await resolveCategoryUuid(data.category),
        ...schemaPayload(data),
      });
      if (response.data) {
        await fetchAll();
        setModal(null);
        pushToast({
          kind: 'success',
          title: t`Saved`,
          msg: t`Action metadata updated.`,
        });
      }
      return response;
    }

    async function createNewVersion({ data }) {
      if (!action || !version) return NO_SELECTION;

      // A version inherits the canonical name (the backend ignores any other).
      // alias / description / category are chain-wide, so send the drawer's
      // values for the backend to propagate to siblings.
      const response = await ActionsApi.createActionVersion(version.uuid, {
        name: action.name,
        alias: data.alias,
        description: data.description,
        category_uuid: await resolveCategoryUuid(data.category),
        ...schemaPayload(data),
      });

      if (response.data) {
        const integrationsToMove = version.integrations.slice();
        // Use the chains returned by fetchAll (the `actions` closure is stale),
        // so the migrate modal and target uuid resolve against the new tip.
        const refreshedActions = (await fetchAll()) || actions;
        const refreshedChain = refreshedActions.find(
          (a) => a.id === action.id,
        ) || { versions: version.versions || [] };
        const newCurrent = currentVersion(refreshedChain) || {
          version: version.version + 1,
        };

        setModal({
          kind: 'migrate',
          actionId: action.id,
          fromVersion: version.version,
          toVersion: newCurrent.version,
          integrationsOnFrom: integrationsToMove,
        });
        pushToast({ kind: 'success', title: t`New version created` });
      }
      // On error, leave the drawer mounted and return the response so its
      // `submit` can surface the error inline (don't unmount mid-await).
      return response;
    }

    async function performMigrate(actionId, fromV, toV, integrationsToMove) {
      const target = actions.find((a) => a.id === actionId);
      if (!target) return;

      const sourceV = target.versions.find((v) => v.version === fromV);
      const toVRef = target.versions.find((v) => v.version === toV);
      if (!sourceV || !toVRef) return;

      const integrationUuids = await resolveIntegrationUuids(
        sourceV.uuid,
        integrationsToMove,
      );
      const response = await ActionsApi.bulkMigrateIntegrations(sourceV.uuid, {
        targetUuid: toVRef.uuid,
        integrationUuids,
      });

      if (response.data) {
        const attrs = response.data.attributes;
        pushToast({
          kind: 'success',
          title: t`Migrated ${attrs.migrated} integration${
            attrs.migrated === 1 ? '' : 's'
          } to v${toV}`,
          msg:
            attrs.rebuild_failed > 0
              ? t`Heads up — ${attrs.rebuild_failed} rebuild job(s) failed to enqueue.`
              : null,
        });
        await fetchAll();
      } else if (response.status === 409) {
        pushToast({
          kind: 'warn',
          title: t`Migration drift detected`,
          msg: t`Some integrations were reassigned mid-flight. Retry from the list.`,
        });
      } else {
        pushToast({
          kind: 'error',
          title: t`Migration failed`,
          msg: response.errors?.[0]?.detail || '',
        });
      }
      setModal(null);
    }

    async function toggleLock() {
      if (!action || !version) return;

      const wasLocked = version.locked;
      const targetUuid = version.uuid;
      const targetVersion = version.version;
      const response = wasLocked
        ? await ActionsApi.unlockAction(targetUuid)
        : await ActionsApi.lockAction(targetUuid);

      if (response.data) {
        // Patched in place rather than refetched: the lock flag is the only
        // thing that changed, and a refetch would close the modal underneath.
        setActions((cur) =>
          cur.map((a) => ({
            ...a,
            versions: a.versions.map((v) =>
              v.uuid === targetUuid ? { ...v, locked: !wasLocked } : v,
            ),
          })),
        );
        setModal(null);
        pushToast({
          kind: wasLocked ? 'success' : 'warn',
          title: wasLocked
            ? t`Unlocked v${targetVersion}`
            : t`Locked v${targetVersion}`,
          msg: wasLocked
            ? t`Available for new integration assignments.`
            : t`Hidden from new integration assignments.`,
        });
      } else if (response.errors) {
        pushToast({
          kind: 'error',
          title: wasLocked ? t`Unlock failed` : t`Lock failed`,
          msg: response.errors[0]?.detail || '',
        });
      }
    }

    async function deleteVersion() {
      if (!action || !version) return;

      const response = await ActionsApi.deleteAction(version.uuid);
      if (response.ok) {
        pushToast({
          kind: 'warn',
          title: t`Deleted v${version.version}`,
          msg: action.name,
        });
        await fetchAll();
        setModal(null);
      } else if (response.errors) {
        pushToast({
          kind: 'error',
          title: response.errors[0]?.title || t`Delete failed`,
          msg: response.errors[0]?.detail || '',
        });
      }
    }

    async function exportText() {
      if (!action || !version) return null;

      const { text, response } = await exportActionDocument(version.uuid);
      if (text == null) {
        pushToast({
          kind: 'error',
          title: t`Export failed`,
          msg:
            response.errors?.[0]?.detail ||
            t`Couldn't export this action. The export endpoint may not be available in this environment yet.`,
        });
      }
      return text;
    }

    async function copyActionExport() {
      const text = await exportText();
      if (text == null) return;
      try {
        await navigator.clipboard.writeText(text);
        pushToast({
          kind: 'success',
          title: t`Copied`,
          msg: t`Action document copied to clipboard.`,
        });
      } catch (e) {
        pushToast({
          kind: 'warn',
          title: t`Clipboard unavailable`,
          msg: t`Couldn't access the clipboard in this browser.`,
        });
      }
    }

    async function downloadActionExport() {
      const text = await exportText();
      if (text == null) return;
      downloadJson(text, `${action.name}.json`);
      pushToast({
        kind: 'success',
        title: t`Downloaded`,
        msg: `${action.name}.json`,
      });
    }

    async function handleImport(doc) {
      const response = await ActionsApi.importAction(doc);
      if (response.errors || !response.data) {
        return { errors: response.errors || [{}] };
      }

      const attrs = response.data;
      if (attrs.status === 'created') {
        pushToast({
          kind: 'success',
          title: t`Action imported`,
          msg: `${attrs.name} created.`,
        });
      } else {
        pushToast({
          kind: 'warn',
          title: t`Already present`,
          msg: `${attrs.name} already exists here — skipped.`,
        });
      }
      await fetchAll();
      return response;
    }

    return {
      createAction,
      editInPlace,
      createNewVersion,
      performMigrate,
      toggleLock,
      deleteVersion,
      copyActionExport,
      downloadActionExport,
      handleImport,
    };
  }, [
    actions,
    setActions,
    action,
    version,
    fetchAll,
    pushToast,
    setModal,
    onCreated,
  ]);
}
